// Pure business rules shared by the API, the web app and the tests.
// Money = integer cents. Hours = integer hundredths. Percentages = basis points.

export type LaborLine = { description: string; hours: number; rate: number; taxable?: unknown; misc?: unknown };
/** A misc / fee line (call fee, disposal…) — stored with labor, totaled apart. */
export const isMisc = (l: { misc?: unknown }) => l.misc === true || Number(l.misc) === 1;
export type PartLine = { description: string; qty: number; unit_price: number; unit_cost: number; taxable?: unknown };

export const laborAmount = (l: { hours: number; rate: number }) => Math.round((l.hours * l.rate) / 100);
/** How a labor line's quantity reads: "" for flat price, "2" for each-priced (tire balance × 2), "1.50 h" for hourly. */
export const laborQty = (l: { hours: number; flat?: number | boolean; unit?: string }) =>
  l.flat ? '' : l.unit === 'ea' ? String(Math.round(l.hours) / 100).replace(/\.0+$/, '') : `${(l.hours / 100).toFixed(2)} h`;
/** Billed shop hours (flat and each-priced lines aren't hours). */
export const isHourly = (l: { flat?: number | boolean; unit?: string }) => !l.flat && l.unit !== 'ea';
/** Ways to lay out an invoice / estimate. */
export const INVOICE_LAYOUTS = ['by_job', 'separate', 'detailed', 'summary', 'total_only'] as const;
export type InvoiceLayout = (typeof INVOICE_LAYOUTS)[number];
// ───────── Parts markup ─────────
// Markup = profit as a % of COST: cost $100, markup 50% → sell $150.
// Margin = profit as a % of the SELL price: same part, $50 / $150 = 33% margin.
/** One row of the markup matrix: parts costing up to `upto` cents (null = anything above) get `pct` % markup. */
export type MarkupTier = { upto: number | null; pct: number };
export const DEFAULT_TIERS: MarkupTier[] = [
  { upto: 500, pct: 100 }, { upto: 2000, pct: 75 }, { upto: 5000, pct: 60 }, { upto: 10000, pct: 50 },
  { upto: 30000, pct: 40 }, { upto: 100000, pct: 30 }, { upto: null, pct: 25 },
];
export function parseTiers(json?: string): MarkupTier[] {
  try {
    const t = (JSON.parse(json || '[]') as MarkupTier[]).filter(x => x && Number.isFinite(Number(x.pct)))
      .map(x => ({ upto: x.upto === null || x.upto === undefined ? null : Math.max(0, Math.round(Number(x.upto))), pct: Math.max(0, Math.round(Number(x.pct))) }));
    return t.sort((a, b) => (a.upto ?? Infinity) - (b.upto ?? Infinity));
  } catch { return []; }
}
type Pricing = { parts_markup_mode?: string; parts_markup_pct?: string | number; parts_markup_tiers?: string };
/** Markup % that applies to a part with this cost (0 = no automatic price). */
export function markupFor(cost: number, s: Pricing) {
  if (s.parts_markup_mode === 'off') return 0;
  if (s.parts_markup_mode === 'tiers') {
    const tiers = parseTiers(s.parts_markup_tiers);
    return (tiers.find(t => t.upto === null || cost <= t.upto) || tiers[tiers.length - 1])?.pct || 0;
  }
  return Number(s.parts_markup_pct || 0);
}
/** Suggested sell price from cost (cents), or 0 when there's no markup rule. */
export const priceFromCost = (cost: number, s: Pricing) => { const p = markupFor(cost, s); return cost > 0 && p > 0 ? Math.round((cost * (100 + p)) / 100) : 0; };
export const markupOf = (cost: number, price: number) => (cost > 0 && price > 0 ? Math.round(((price - cost) / cost) * 100) : null);
export const marginOf = (cost: number, price: number) => (cost > 0 && price > 0 ? Math.round(((price - cost) / price) * 100) : null);

/** Truck / trailer types (the first group are power units). "Truck" / "Trailer" are kept for older records. */
export const TRUCK_TYPES = ['Sleeper truck', 'Day cab', 'Box truck', 'Bus', 'Pickup / service truck'] as const;
export const TRAILER_TYPES = ['Dry van', 'Reefer', 'Flatbed', 'Step deck', 'Tanker', 'Other trailer'] as const;
export const UNIT_TYPES = [...TRUCK_TYPES, ...TRAILER_TYPES, 'Other', 'Truck', 'Trailer'] as const;
export const isTrailerType = (t?: string) => !!t && ((TRAILER_TYPES as readonly string[]).includes(t) || t === 'Trailer');

export const partAmount = (p: { qty: number; unit_price: number }) => p.qty * p.unit_price;

/** What shop supplies % and sales tax are charged on. */
export type ChargeBase = 'labor' | 'parts' | 'both';
export const CHARGE_BASES: { value: ChargeBase; label: string }[] = [
  { value: 'labor', label: 'Labor only' }, { value: 'parts', label: 'Parts only' }, { value: 'both', label: 'Labor + parts' },
];
export type TotalsOpts = {
  parts_tax_bp: number; supplies_bp: number;
  /** Shop supplies % is taken from… (default labor) */ supplies_on?: ChargeBase;
  /** Shop supplies never more than this (cents); 0 = no cap. */ supplies_max?: number;
  /** Sales tax is charged on… (default parts) */ tax_on?: ChargeBase;
  /** Shop supplies are taxed too. */ tax_supplies?: boolean;
  /** Shop supplies typed by hand for this job (cents) — replaces the % (null = use the %). */ supplies_fixed?: number | null;
};
const base = (on: ChargeBase | undefined, fallback: ChargeBase, labor: number, parts: number) => {
  const b = on || fallback;
  return (b === 'labor' || b === 'both' ? labor : 0) + (b === 'parts' || b === 'both' ? parts : 0);
};
const asBase = (x: unknown, d: ChargeBase): ChargeBase => (x === 'labor' || x === 'parts' || x === 'both' ? x : d);
/** Totals options from the shop's settings. */
export const totalsOpts = (s: Record<string, unknown>): TotalsOpts => ({
  parts_tax_bp: Number(s.parts_tax_bp || 0), supplies_bp: Number(s.supplies_bp || 0),
  supplies_on: asBase(s.supplies_on, 'labor'), supplies_max: Number(s.supplies_max || 0),
  tax_on: asBase(s.tax_on, 'parts'), tax_supplies: String(s.tax_supplies) === '1',
});

/** Is this line taxed? A line's own tick wins; otherwise Settings → "Sales tax is charged on". */
export function isTaxable(kind: 'labor' | 'parts' | 'misc', line: { taxable?: unknown }, tax_on: ChargeBase = 'parts') {
  if (line.taxable !== null && line.taxable !== undefined && line.taxable !== '') return line.taxable === true || Number(line.taxable) === 1;
  if (kind === 'misc') return false; // fees aren't taxed unless ticked
  return tax_on === 'both' || tax_on === kind;
}
/** The tax rate for one job: its own choice, else 0 for a tax-exempt customer, else the shop rate. */
export const jobTaxRate = (shopBp: unknown, woBp: unknown, customerExempt: unknown) =>
  woBp !== null && woBp !== undefined && woBp !== '' ? Number(woBp) : Number(customerExempt) ? 0 : Number(shopBp || 0);
/** "7.8%" from basis points. */
export const pctLabel = (bp: number) => `${Number((bp / 100).toFixed(3))}%`;

/** Lines with their taxable tick worked out (true/false), the job's tax rate, and whether it's tax exempt. */
export function taxedLines<L extends object, P extends object>(s: Record<string, unknown>, woBp: unknown, custExempt: unknown, labor: L[], parts: P[], suppliesFixed?: unknown) {
  const opts = totalsOpts(s);
  const rate = jobTaxRate(s.parts_tax_bp, woBp, custExempt);
  return {
    opts: { ...opts, parts_tax_bp: rate, supplies_fixed: suppliesFixed === null || suppliesFixed === undefined || suppliesFixed === '' ? null : Number(suppliesFixed) },
    exempt: rate === 0 && (Number(woBp) === 0 && woBp !== null && woBp !== undefined || !!Number(custExempt)),
    labor: labor.map(l => ({ ...l, taxable: isTaxable(isMisc(l as { misc?: unknown }) ? 'misc' : 'labor', l as { taxable?: unknown }, opts.tax_on) })),
    parts: parts.map(p => ({ ...p, taxable: isTaxable('parts', p as { taxable?: unknown }, opts.tax_on) })),
  };
}

export function invoiceTotals(
  labor: LaborLine[],
  parts: PartLine[],
  opts: TotalsOpts,
) {
  const labor_total = labor.reduce((a, l) => a + (isMisc(l) ? 0 : laborAmount(l)), 0);
  const misc_total = labor.reduce((a, l) => a + (isMisc(l) ? laborAmount(l) : 0), 0);
  const parts_total = parts.reduce((a, p) => a + partAmount(p), 0);
  let supplies = Math.round((base(opts.supplies_on, 'labor', labor_total, parts_total) * opts.supplies_bp) / 10000);
  if (opts.supplies_max && supplies > opts.supplies_max) supplies = opts.supplies_max;
  if (opts.supplies_fixed !== null && opts.supplies_fixed !== undefined && !Number.isNaN(Number(opts.supplies_fixed))) supplies = Number(opts.supplies_fixed);
  const taxable = labor.reduce((a, l) => a + (isTaxable(isMisc(l) ? 'misc' : 'labor', l, opts.tax_on) ? laborAmount(l) : 0), 0)
    + parts.reduce((a, p) => a + (isTaxable('parts', p, opts.tax_on) ? partAmount(p) : 0), 0)
    + (opts.tax_supplies ? supplies : 0);
  const tax = Math.round((taxable * opts.parts_tax_bp) / 10000);
  const parts_cost = parts.reduce((a, p) => a + p.qty * p.unit_cost, 0);
  return { labor_total, parts_total, misc_total, supplies, tax, total: labor_total + parts_total + misc_total + supplies + tax, parts_cost };
}

export type PayType = 'hourly' | 'salary_weekly' | 'salary_monthly' | 'percent_labor' | 'per_job' | 'none';
export const PAY_TYPES: { value: PayType; label: string; unit: string }[] = [
  { value: 'hourly', label: 'Hourly (time clock)', unit: '$ / hour' },
  { value: 'salary_weekly', label: 'Weekly salary', unit: '$ / week' },
  { value: 'salary_monthly', label: 'Monthly salary', unit: '$ / month' },
  { value: 'percent_labor', label: '% of labor billed', unit: '% of labor' },
  { value: 'per_job', label: 'Flat per completed job', unit: '$ / job' },
  { value: 'none', label: 'Not on payroll', unit: '' },
];

/** Milliseconds → hundredths of an hour (rounded to nearest 0.01h). */
export const msToHundredths = (ms: number) => Math.round(ms / 36000);

const DAY = 86400000;
const parseDay = (d: string) => Date.parse(d + 'T00:00:00Z');
const dayStr = (t: number) => new Date(t).toISOString().slice(0, 10);

/** Monday (YYYY-MM-DD) of the week containing the given date. */
export function weekStart(date: string) {
  const t = parseDay(date);
  const dow = (new Date(t).getUTCDay() + 6) % 7; // Mon=0
  return dayStr(t - dow * DAY);
}

/** Inclusive number of days in a period. */
export const periodDays = (start: string, end: string) => Math.round((parseDay(end) - parseDay(start)) / DAY) + 1;

/**
 * Split worked hours into regular/overtime using the 40h-per-week rule.
 * `daily` maps local date (YYYY-MM-DD) → hundredths worked that day.
 */
export function splitOvertime(daily: Record<string, number>, overtime: boolean) {
  if (!overtime) {
    const total = Object.values(daily).reduce((a, b) => a + b, 0);
    return { regular: total, overtime: 0 };
  }
  const weeks: Record<string, number> = {};
  for (const [d, h] of Object.entries(daily)) weeks[weekStart(d)] = (weeks[weekStart(d)] || 0) + h;
  let regular = 0, ot = 0;
  for (const h of Object.values(weeks)) {
    regular += Math.min(h, 4000);
    ot += Math.max(0, h - 4000);
  }
  return { regular, overtime: ot };
}

/**
 * Base pay for one person for one pay period.
 * - hourly: regular × rate + overtime × 1.5 × rate
 * - salary_weekly: rate × (days / 7)
 * - salary_monthly: rate × 12 / 52 per week → × (days / 7)
 * - percent_labor: bp × labor billed on their completed jobs
 * - per_job: rate × completed jobs
 */
export function basePay(input: {
  pay_type: PayType;
  pay_rate: number;
  regular: number;
  overtime: number;
  days: number;
  labor_billed: number;
  jobs: number;
}) {
  const { pay_type, pay_rate, regular, overtime, days } = input;
  switch (pay_type) {
    case 'hourly':
      return Math.round((regular * pay_rate) / 100 + (overtime * pay_rate * 1.5) / 100);
    case 'salary_weekly':
      return Math.round((pay_rate * days) / 7);
    case 'salary_monthly':
      return Math.round(((pay_rate * 12) / 52) * (days / 7));
    case 'percent_labor':
      return Math.round((input.labor_billed * pay_rate) / 10000);
    case 'per_job':
      return pay_rate * input.jobs;
    default:
      return 0;
  }
}

export const dollars = (cents: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format((cents || 0) / 100);
export const hours = (h: number) => ((h || 0) / 100).toFixed(2);

export const WO_STATUSES = ['New', 'Diagnosing', 'Waiting Parts', 'In Progress', 'Ready', 'Completed'] as const;
export type WoStatus = (typeof WO_STATUSES)[number];
/** Statuses people pick and see. "Waiting Approval" = the customer has to OK more work (kept as a flag next to the status). */
export const WO_STATUS_VIEW = ['New', 'Diagnosing', 'Waiting Approval', 'Waiting Parts', 'In Progress', 'Ready', 'Completed'] as const;

// ───────── Schedule, attendance & overhead ─────────
export type Schedule = { work_days: number; shift_start: string; shift_end: string; absent_deducts: boolean; late_deducts: boolean; early_deducts?: boolean; extra_rate_pct: number };

export function scheduleFrom(s: Record<string, string>): Schedule {
  return {
    work_days: Number(s.work_days || 6),
    shift_start: s.shift_start || '09:00',
    shift_end: s.shift_end || '19:00',
    absent_deducts: s.absent_deducts === '1',
    late_deducts: s.late_deducts !== '0',
    early_deducts: s.early_deducts === '1',
    extra_rate_pct: Number(s.extra_rate_pct || 100),
  };
}

const toMin = (hhmm: string) => { const [h, m] = hhmm.split(':').map(Number); return h * 60 + (m || 0); };
/** Shift length in hundredths of an hour (09:00–19:00 → 1000). */
export const shiftHours = (s: Schedule) => Math.max(0, Math.round(((toMin(s.shift_end) - toMin(s.shift_start)) / 60) * 100));

/** Mon=0 … Sun=6 for a YYYY-MM-DD date. */
export const dayOfWeek = (d: string) => (new Date(d + 'T12:00:00Z').getUTCDay() + 6) % 7;
export const isWorkDay = (d: string, workDays: number) => dayOfWeek(d) < workDays;

export function workDaysBetween(start: string, end: string, workDays: number) {
  let n = 0;
  for (let t = Date.parse(start + 'T12:00:00Z'); t <= Date.parse(end + 'T12:00:00Z'); t += DAY) if (isWorkDay(dayStr(t), workDays)) n++;
  return n;
}

/** What one hour of this person's time costs, in cents (salaries converted using the shop schedule). */
export function hourlyEquivalent(pay_type: PayType, pay_rate: number, s: Schedule) {
  const weekHours = (s.work_days * shiftHours(s)) / 100;
  if (pay_type === 'hourly') return pay_rate;
  if (!weekHours) return 0;
  if (pay_type === 'salary_weekly') return pay_rate / weekHours;
  if (pay_type === 'salary_monthly') return (pay_rate * 12) / 52 / weekHours;
  return 0;
}

export type Mark = { day: string; status: string; late_minutes: number; extra_hours: number; early_minutes?: number; early_ok?: number; half_day?: number };

/**
 * Pay change from attendance marks. Applies to salaried staff only —
 * hourly staff are already paid exactly what the time clock shows.
 */
export function attendanceAdjustment(pay_type: PayType, pay_rate: number, marks: Mark[], s: Schedule, absentRate = 0) {
  const salaried = pay_type === 'salary_weekly' || pay_type === 'salary_monthly';
  const absent = marks.filter(m => m.status === 'absent').length;
  const halves = marks.filter(m => m.half_day && (m.status === 'present' || m.status === 'late')).length;
  const lateMin = marks.reduce((a, m) => a + (m.status === 'late' ? m.late_minutes : 0), 0);
  // Left before the shift ended: with the owner's OK it costs nothing; on their own it is deducted like late minutes (when that's on).
  const earlyOk = marks.reduce((a, m) => a + (m.early_minutes && m.early_ok !== 0 && !m.half_day ? m.early_minutes : 0), 0);
  const earlyOwn = marks.reduce((a, m) => a + (m.early_minutes && m.early_ok === 0 && !m.half_day ? m.early_minutes : 0), 0);
  const extra = marks.reduce((a, m) => a + (m.status === 'present' || m.status === 'late' ? m.extra_hours : 0), 0);
  const parts: string[] = [];
  if (absent) parts.push(`${absent} absent`);
  if (halves) parts.push(`${halves} half day${halves > 1 ? 's' : ''}`);
  if (lateMin) parts.push(`${lateMin} min late`);
  if (earlyOwn) parts.push(`left ${earlyOwn} min early`);
  if (earlyOk) parts.push(`left ${earlyOk} min early (OK'd)`);
  if (extra) parts.push(`${(extra / 100).toFixed(2)} h extra`);
  if (!salaried) return { amount: 0, note: parts.length ? parts.join(', ') + ' (paid by time clock)' : '' };
  const hr = hourlyEquivalent(pay_type, pay_rate, s);
  const day = absentRate || dayRate(pay_type, pay_rate, s);
  let amount = 0;
  if (s.absent_deducts) amount -= absent * day;
  amount -= halves * day / 2;
  if (s.late_deducts) amount -= (lateMin / 60) * hr;
  if (s.early_deducts) amount -= (earlyOwn / 60) * hr;
  amount += (extra / 100) * hr * (s.extra_rate_pct / 100);
  return { amount: Math.round(amount), note: parts.join(', ') };
}

/** One day's pay for salaried staff (weekly salary ÷ work days). */
export function dayRate(pay_type: PayType, pay_rate: number, s: Schedule) {
  if (pay_type === 'salary_weekly') return pay_rate / s.work_days;
  if (pay_type === 'salary_monthly') return (pay_rate * 12) / 52 / s.work_days;
  return 0;
}

/** Monthly equivalent of a repeating bill. Daily bills count every work day. */
export function recurringMonthly(amount: number, frequency: string, workDaysInMonth: number) {
  if (frequency === 'weekly') return Math.round((amount * 52) / 12);
  if (frequency === 'daily') return amount * workDaysInMonth;
  return amount;
}

/** Rough monthly cost of a person, for break-even targets. weeklyHours (hundredths) is for part-timers. */
export function monthlyStaffCost(pay_type: PayType, pay_rate: number, workDaysInMonth: number, s: Schedule, weeklyHours = 0) {
  switch (pay_type) {
    case 'salary_weekly': return Math.round((pay_rate * 52) / 12);
    case 'salary_monthly': return pay_rate;
    case 'hourly': return weeklyHours ? Math.round((pay_rate * weeklyHours * 52) / 12 / 100) : Math.round((pay_rate * shiftHours(s) * workDaysInMonth) / 100);
    default: return 0;
  }
}

export const EXPENSE_CATEGORIES = [
  'Rent', 'Utilities (power, water, gas)', 'Internet & phone', 'Insurance', 'Shop supplies', 'Tools & equipment',
  'Blades, bits & small tools', 'Cleaning supplies',
  'Fuel', 'Shop truck / vehicles', 'Advertising', 'Software & subscriptions', 'Taxes & licenses', 'Repairs & maintenance',
  'Bank & card fees', 'Meals', 'Other',
] as const;

/** Store-run categories (Harbor Freight, Home Depot…) — what mechanics pick from on the phone. */
export const PURCHASE_CATEGORIES = ['Tools & equipment', 'Blades, bits & small tools', 'Cleaning supplies', 'Shop supplies', 'Repairs & maintenance', 'Other'] as const;

/** Invoices count as overdue only after the grace days (Settings → Invoices, default 7). Returns the due date before which it's overdue. */
export function overdueBefore(today: string, grace: unknown) {
  const g = grace === undefined || grace === null || grace === '' ? 7 : Math.max(0, Math.min(90, Math.round(Number(grace) || 0)));
  const d = new Date(today + 'T12:00:00Z'); d.setUTCDate(d.getUTCDate() - g);
  return d.toISOString().slice(0, 10);
}
/** "INV-99" before "INV-376": compare the numbers, not the text. */
export const numCompare = (a: string, b: string) => String(a || '').localeCompare(String(b || ''), undefined, { numeric: true, sensitivity: 'base' });

/** Lines of warranty jobs at $0 for the customer (labor rate and part price) — cost and hours stay for the shop's numbers. */
export function warrantyFree<L extends { job_id: string | null; rate: number }, P extends { job_id: string | null; unit_price: number }>(jobs: { id: string; warranty?: number }[], labor: L[], parts: P[]) {
  const w = new Set(jobs.filter(j => j.warranty).map(j => j.id));
  if (!w.size) return { labor, parts };
  return { labor: labor.map(l => (l.job_id && w.has(l.job_id) ? { ...l, rate: 0 } : l)), parts: parts.map(p => (p.job_id && w.has(p.job_id) ? { ...p, unit_price: 0 } : p)) };
}
